import { EmptyState } from '@dev-spendesk/grapes';
import React, { useMemo, useState } from 'react';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications, NotificationType } from 'modules/app/notifications';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { type AnalyticalFieldValueMappingV2 } from 'src/core/modules/bookkeep';
import { useDesignatedCustomFieldQuery } from 'src/core/modules/custom-fields/hooks/useDesignatedCustomFieldQuery';

import { useCreateEcQuery } from './hooks/useCreateEcQuery';
import { EcFormModal } from '../../components/EcFormModal';
import { type FormValues } from '../../components/EcFormModal/formValues';
import { useExpenseCategoriesQuery } from '../EcContainer/hooks/useExpenseCategoriesQuery';

type Props = {
  isOpen: boolean;
  onComplete(): void;
  onCancel(): void;
  isMapSyncEnabled: boolean;
  isSplittable: boolean;
};

export const EcCreationFormModalContainer = ({
  isOpen,
  onComplete,
  onCancel,
  isMapSyncEnabled,
  isSplittable,
}: Props) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const [createExpenseCategory] = useCreateEcQuery();

  const designatedCustomFieldQueryState = useDesignatedCustomFieldQuery();
  const designatedCustomFieldId = unwrapQuery(
    designatedCustomFieldQueryState,
  )?.customFieldId;

  const queryStates = useQueryStates({
    states: {
      costCenters: useCostCentersQuery(),
      expenseCategories: useExpenseCategoriesQuery(),
    },
    reshapeData: ({ costCenters, expenseCategories }) => ({
      costCenters,
      expenseCategories,
    }),
  });

  const [valueMapping, setValueMapping] =
    useState<AnalyticalFieldValueMappingV2>();

  const {
    analyticalFieldMappings,
    syncMode,
    fieldMapping,
    setFieldMapping,
    onSaveChanges,
    handleValueMapping,
  } = useAnalyticalFieldMapping({
    field: {
      id: designatedCustomFieldId as string,
      name: t(
        'accounting-integration.exportSettings.fieldTypes.expenseCategory',
      ),
    },
    fieldType: 'expenseCategory',
  });

  const valueInDatabase = useMemo(
    () =>
      fieldMapping?.values.find(
        (value) => value.internalId === designatedCustomFieldId,
      ),
    [fieldMapping, designatedCustomFieldId],
  );

  const handleSubmit = async ({ name, costCenterIds }: FormValues) => {
    const newCategory = await createExpenseCategory({
      name,
      costCenterIds,
    });

    if (isMapSyncEnabled && fieldMapping) {
      handleValueMapping({ value: valueMapping, internalId: newCategory.id });
    }
  };

  const handleCancel = () => {
    setValueMapping(undefined);
    onCancel();
  };

  return (
    <QuerySuspense
      queryState={queryStates}
      fallback={() => (
        <EmptyState
          title={t('misc.errors.networkError')}
          subtitle=""
          iconVariant="alert"
          iconName="hexagone-cross"
        />
      )}
      loading={null}
    >
      {({ costCenters, expenseCategories }) => {
        return (
          <EcFormModal
            costCenters={costCenters}
            expenseCategoryNames={expenseCategories.map((ec) => ec.name)}
            isOpen={isOpen}
            isMapSyncEnabled={isMapSyncEnabled}
            isSplittable={isSplittable}
            designatedCustomFieldId={designatedCustomFieldId}
            onSubmit={async (values) => {
              try {
                await handleSubmit(values);
                onComplete();
              } catch {
                pushNotif({
                  type: NotificationType.Danger,
                  message: t('expenseCategories.form.creationError'),
                });
              }
            }}
            onCancel={handleCancel}
            analyticalFieldMappings={analyticalFieldMappings}
            fieldMapping={fieldMapping}
            setFieldMapping={setFieldMapping}
            onSaveChanges={onSaveChanges}
            valueMapping={valueMapping}
            setValueMapping={setValueMapping}
            initialSyncMode={syncMode}
            valueInDatabase={valueInDatabase}
          />
        );
      }}
    </QuerySuspense>
  );
};
